import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Bell, MessageCircle, Settings, TestTube, CheckCircle,
  AlertCircle, Clock, Smartphone, Loader2, Save,
  RefreshCw, QrCode, Link as LinkIcon, Shield, Info
} from 'lucide-react';
import api from '../utils/api';
import { InlineAlert, LoadingState, PageHeader } from '../components/ui';
import { useToast } from '../components/ToastProvider';

export default function WhatsAppConfig() {
  const { id } = useParams();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [whatsappMode, setWhatsappMode] = useState('embedded');
  const [qrCodeData, setQrCodeData] = useState(null);
  const [lastCheck, setLastCheck] = useState(null);

  const [config, setConfig] = useState({
    whatsapp_number: '',
    whatsapp_notifications_enabled: true,
    reminder_enabled: true,
    reminder_hours_before: 2
  });

  // Verificar status da conexão periodicamente
  const checkConnectionStatus = useCallback(async () => {
    try {
      const response = await api.get(`/api/whatsapp/status/${id}`);
      if (response.data.success) {
        setConnectionStatus(response.data.status || 'disconnected');
        setWhatsappMode(response.data.mode || 'embedded');
        setQrCodeData(response.data.qrCodeImage || null);
        if (response.data.error) {
          setError(response.data.error);
        }
      }
    } catch (err) {
      console.error('Erro ao verificar status:', err);
    }
  }, [id]);

  useEffect(() => {
    loadConfig();
    checkConnectionStatus();

    // Verificar status a cada 5 segundos
    const interval = setInterval(() => {
      checkConnectionStatus();
    }, 5000);

    return () => clearInterval(interval);
  }, [id, checkConnectionStatus]);

  const loadConfig = async () => {
    try {
      const response = await api.get(`/config/whatsapp`, { params: { id } });
      setConfig(prev => ({
        ...prev,
        whatsapp_notifications_enabled: response.data.notifications_enabled,
        reminder_enabled: response.data.reminder_enabled,
        reminder_hours_before: response.data.reminder_hours_before,
        whatsapp_number: response.data.whatsapp_number || ''
      }));
      setLoading(false);
    } catch (err) {
      console.error('Erro ao carregar configurações:', err);
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.put(`/config/whatsapp`,
        {
          whatsapp_number: config.whatsapp_number,
          whatsapp_notifications_enabled: config.whatsapp_notifications_enabled,
          reminder_enabled: config.reminder_enabled,
          reminder_hours_before: config.reminder_hours_before
        },
        { params: { id } }
      );

      setSuccess(response.data.message);
      toast.success(response.data.message || 'Configuracoes salvas.');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post(`/config/whatsapp/test`,
        { test_message: '🧪 Teste de configuração do WhatsApp' },
        { params: { id } }
      );

      setSuccess(response.data.message);
      toast.success(response.data.message || 'Configuracoes salvas.');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao enviar mensagem de teste');
    } finally {
      setTesting(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post(`/api/whatsapp/connect/${id}`);

      setSuccess(response.data.instructions || response.data.message || 'Conexao iniciada. Veja o QR Code no worker.');
      toast.success('Conexao iniciada.');
      // Aguardar um momento e verificar status
      setTimeout(() => {
        checkConnectionStatus();
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao conectar ao WhatsApp');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setConnecting(true);
    setError('');
    setSuccess('');

    try {
      await api.post(`/api/whatsapp/disconnect/${id}`);
      setSuccess('WhatsApp desconectado com sucesso');
      toast.success('WhatsApp desconectado.');
      setQrCodeData(null);
      setTimeout(() => {
        checkConnectionStatus();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao desconectar');
    } finally {
      setConnecting(false);
    }
  };

  const handleReconnect = async () => {
    setConnecting(true);
    setError('');
    setSuccess('');

    try {
      await api.post(`/api/whatsapp/reconnect/${id}`);
      setSuccess('Reconexao iniciada! Escaneie o novo QR Code.');
      toast.success('Reconexao iniciada.');
      setTimeout(() => {
        checkConnectionStatus();
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao reconectar');
    } finally {
      setConnecting(false);
    }
  };

  const getStatusBadge = () => {
    switch (connectionStatus) {
      case 'connected':
        return (
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-green-500/10 text-green-400 border border-green-500/20">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            Conectado
          </span>
        );
      case 'connecting':
      case 'authenticated':
        return (
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">
            <Loader2 className="w-3 h-3 animate-spin" />
            {connectionStatus === 'authenticated' ? 'Finalizando...' : 'Conectando...'}
          </span>
        );
      case 'waiting_qr':
        return (
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
            <QrCode className="w-3 h-3" />
            Aguardando QR Code
          </span>
        );
      case 'worker_mode':
      case 'starting':
        return (
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
            <Loader2 className="w-3 h-3 animate-spin" />
            Worker externo ativo
          </span>
        );
      case 'offline':
        return (
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">
            <span className="w-2 h-2 rounded-full bg-yellow-400" />
            Worker offline
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-red-500/10 text-red-400 border border-red-500/20">
            <span className="w-2 h-2 rounded-full bg-red-400" />
            Desconectado
          </span>
        );
    }
  };

  const isWorkerMode = whatsappMode === 'worker';
  const canTestSend = !!config.whatsapp_number && connectionStatus === 'connected';
  const shouldShowWorkerConnect = isWorkerMode && connectionStatus !== 'connected';

  if (loading) { return <LoadingState label="Carregando WhatsApp..." />; }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Automacao"
        title="WhatsApp Web"
        description="Conecte o WhatsApp da barbearia, acompanhe status do worker e controle notificacoes automaticas."
        icon={MessageCircle}
      />

      {/* Status da Conexão WhatsApp */}
      <div className="card" data-tour="whatsapp-status">
        <div className="p-6 border-b border-dark-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageCircle className="w-5 h-5 text-primary-400" />
              <h2 className="text-lg font-semibold text-white">Status do WhatsApp Web</h2>
            </div>
            {getStatusBadge()}
          </div>
          <p className="text-gray-400 mt-2 text-sm">
            O sistema utiliza WhatsApp Web via QR Code para enviar notificações automaticamente
          </p>
        </div>

        <div className="p-6 space-y-4">
          {/* QR Code Area */}
          {connectionStatus === 'waiting_qr' && !qrCodeData && (
            <div className="flex flex-col items-center p-6 bg-dark-700 rounded-xl border border-yellow-500/20" data-tour="whatsapp-qr">
              <Loader2 className="w-8 h-8 text-yellow-400 animate-spin mb-4" />
              <p className="text-sm text-yellow-400">Gerando QR Code... Aguarde.</p>
            </div>
          )}

          {qrCodeData && connectionStatus !== 'connected' && (
            <div className="flex flex-col items-center p-6 bg-dark-700 rounded-xl border border-blue-500/20" data-tour="whatsapp-qr">
              <p className="text-sm text-gray-400 mb-4">Escaneie este QR Code com o WhatsApp do seu celular:</p>
              <div className="bg-white p-4 rounded-lg inline-block">
                <img
                  src={qrCodeData}
                  alt="QR Code WhatsApp"
                  className="w-48 h-48"
                />
              </div>
              <p className="text-xs text-gray-500 mt-4">
                📱 No celular: WhatsApp → Configurações → Aparelhos conectados → Vincular dispositivo
              </p>
              <p className="text-xs text-yellow-500 mt-2">
                ⚠️ Este QR Code expira em alguns segundos. Se expirar, clique em "Conectar WhatsApp Web" novamente.
              </p>
            </div>
          )}

          {connectionStatus === 'connected' && (
            <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20 flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
              <div>
                <p className="text-green-400 font-medium">WhatsApp conectado com sucesso!</p>
                <p className="text-sm text-green-300 mt-1">
                  As notificações serão enviadas automaticamente para o número configurado.
                </p>
              </div>
            </div>
          )}

          {shouldShowWorkerConnect && (
            <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-start gap-3">
              <Smartphone className="w-5 h-5 text-blue-400 mt-0.5" />
              <div className="flex-1">
                <p className="text-blue-400 font-medium">Conectar WhatsApp Web</p>
                <p className="text-sm text-blue-300 mt-1 mb-3">
                  Clique para gerar o QR Code desta barbearia. O worker global vai criar uma sessao separada para ela.
                </p>
                <button
                  onClick={handleConnect}
                  disabled={connecting}
                  className="btn-primary flex items-center gap-2"
                >
                  {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />}
                  {connecting ? 'Conectando...' : 'Conectar WhatsApp Web'}
                </button>
              </div>
            </div>
          )}

          {connectionStatus === 'disconnected' && !isWorkerMode && (
            <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20 flex items-start gap-3">
              <Info className="w-5 h-5 text-yellow-400 mt-0.5" />
              <div className="flex-1">
                <p className="text-yellow-400 font-medium">WhatsApp não conectado</p>
                <p className="text-sm text-yellow-300 mt-1 mb-3">
                  Conecte o WhatsApp Web para ativar as notificações automáticas de agendamentos.
                </p>
                <button
                  onClick={handleConnect}
                  disabled={connecting}
                  className="btn-primary flex items-center gap-2"
                >
                  {connecting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <QrCode className="w-4 h-4" />
                  )}
                  {connecting ? 'Conectando...' : 'Conectar WhatsApp Web'}
                </button>
              </div>
            </div>
          )}

          {/* Controles de conexão */}
          {connectionStatus === 'connected' && (
            <div className="flex gap-3">
              <button
                onClick={handleReconnect}
                disabled={connecting}
                className="btn-secondary flex-1 flex items-center justify-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${connecting ? 'animate-spin' : ''}`} />
                {connecting ? 'Reconectando...' : 'Reconectar (Novo QR Code)'}
              </button>
              <button
                onClick={handleDisconnect}
                disabled={connecting}
                className="btn-secondary flex-1 flex items-center justify-center gap-2 text-red-400 hover:text-red-400"
              >
                Desconectar
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Credenciais e Número */}
      <div className="card">
        <div className="p-6 border-b border-dark-600">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-primary-400" />
            Configurações do WhatsApp
          </h2>
          <p className="text-gray-400 mt-1">
            Configure o número de WhatsApp da barbearia e as notificações
          </p>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-6">
          {/* Número do WhatsApp */}
          <div>
            <label className="label">Número do WhatsApp da Barbearia</label>
            <input
              type="tel"
              placeholder="(11) 99999-9999"
              className="input"
              value={config.whatsapp_number}
              onChange={(e) => setConfig({ ...config, whatsapp_number: e.target.value })}
            />
            <p className="text-xs text-gray-500 mt-1">
              Número onde a barbearia receberá notificações e clientes poderão entrar em contato
            </p>
          </div>

          {/* Configurações de Notificação */}
          <div>
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <Bell className="w-4 h-4 text-primary-400" />
              Configurações de Notificação
            </h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-dark-700 rounded-xl">
                <div className="flex items-center gap-3">
                  <MessageCircle className="w-5 h-5 text-primary-400" />
                  <div>
                    <p className="text-white font-medium">Notificar dono da barbearia</p>
                    <p className="text-sm text-gray-400">Receba alertas de novos agendamentos</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.whatsapp_notifications_enabled}
                    onChange={(e) => setConfig({ ...config, whatsapp_notifications_enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-dark-700 rounded-xl">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-primary-400" />
                  <div>
                    <p className="text-white font-medium">Lembretes para clientes</p>
                    <p className="text-sm text-gray-400">Envie lembretes automáticos antes dos agendamentos</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.reminder_enabled}
                    onChange={(e) => setConfig({ ...config, reminder_enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              {config.reminder_enabled && (
                <div className="p-4 bg-dark-700 rounded-xl">
                  <label className="label">Horas antes do agendamento</label>
                  <select
                    className="input"
                    value={config.reminder_hours_before}
                    onChange={(e) => setConfig({ ...config, reminder_hours_before: parseInt(e.target.value) })}
                  >
                    <option value={1}>1 hora antes</option>
                    <option value={2}>2 horas antes</option>
                    <option value={4}>4 horas antes</option>
                    <option value={8}>8 horas antes</option>
                    <option value={24}>24 horas antes</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Mensagens de feedback */}
          {success && (
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center gap-2 text-green-400">
              <CheckCircle className="w-4 h-4" />
              {success}
            </div>
          )}

          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-2 text-red-400">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          {/* Botões */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Salvar Configurações
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleTest}
              disabled={testing || !canTestSend}
              className="btn-secondary flex items-center justify-center gap-2"
            >
              {testing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Testando...
                </>
              ) : (
                <>
                  <TestTube className="w-5 h-5" />
                  Testar Envio
                </>
              )}
            </button>
          </div>

          {/* Nota sobre WhatsApp Web */}
          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-sm text-blue-400">
            💡 <strong>WhatsApp Web:</strong> Este sistema usa conexão direta via WhatsApp Web.
            Conecte seu celular escaneando o QR Code acima para ativar as notificações automáticas.
          </div>
        </form>
      </div>

      {/* Guia de Configuração */}
      <div className="card">
        <div className="p-6">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <Info className="w-4 h-4 text-yellow-400" />
            Como Configurar o WhatsApp
          </h3>

          <div className="space-y-3 text-sm text-gray-400">
            <div className="flex gap-3">
              <span className="text-primary-400 font-medium">1.</span>
              <p>Clique em <strong>"Conectar WhatsApp Web"</strong> acima</p>
            </div>
            <div className="flex gap-3">
              <span className="text-primary-400 font-medium">2.</span>
              <p>Escaneie o QR Code com o WhatsApp do seu celular:</p>
            </div>
            <div className="flex gap-3 ml-7">
              <p className="text-gray-500">→ Abra o WhatsApp no celular</p>
            </div>
            <div className="flex gap-3 ml-7">
              <p className="text-gray-500">→ Configurações (⚙️)</p>
            </div>
            <div className="flex gap-3 ml-7">
              <p className="text-gray-500">→ Aparelhos conectados</p>
            </div>
            <div className="flex gap-3 ml-7">
              <p className="text-gray-500">→ Vincular dispositivo → Escaneie o QR Code</p>
            </div>
            <div className="flex gap-3">
              <span className="text-primary-400 font-medium">3.</span>
              <p>Informe seu número de WhatsApp no campo acima e salve</p>
            </div>
            <div className="flex gap-3">
              <span className="text-primary-400 font-medium">4.</span>
              <p>Teste o envio clicando em "Testar Envio"</p>
            </div>
            <div className="flex gap-3">
              <span className="text-primary-400 font-medium">5.</span>
              <p>O sistema enviará notificações automaticamente para cada novo agendamento</p>
            </div>
          </div>

          <div className="mt-4 p-3 rounded-lg bg-dark-700 border border-dark-600">
            <p className="text-yellow-400 text-xs">
              ⚠️ <strong>Importante:</strong> Mantenha o celular com WhatsApp conectado à internet
              para receber as notificações dos barbeiros. O WhatsApp Web permanece ativo enquanto
              a sessão estiver conectada.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}


